import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import PageHeader from "@/components/PageHeader";
import SecondaryNav from "@/components/SecondaryNav";
import ProvenanceBadge from "@/components/ProvenanceBadge";
import TradeWizard from "@/pages/TradeWizard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Search, FileEdit, Archive, RefreshCw, Link2, Filter, Paperclip, Layers, List as ListIcon, TrendingUp, TrendingDown, ChevronRight, Wrench, Loader2 } from "lucide-react";
import { fmtEUR, fmtNum, fmtDateTime } from "@/lib/format";
import { deriveQuoteCurrency, fmtMoney } from "@/lib/currency";
import { toast } from "sonner";
import EnrichDialog from "@/pages/EnrichDialog";
import TradeDetailDialog from "@/pages/TradeDetailDialog";
import PositionDetailDialog from "@/pages/PositionDetailDialog";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtRelative(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diffSec = Math.floor((Date.now() - then) / 1000);
  if (diffSec < 30) return "gerade eben";
  if (diffSec < 90) return "vor 1 min";
  if (diffSec < 3600) return `vor ${Math.round(diffSec / 60)} min`;
  if (diffSec < 90 * 60) return "vor 1 Std.";
  if (diffSec < 24 * 3600) return `vor ${Math.round(diffSec / 3600)} Std.`;
  if (diffSec < 48 * 3600) return "gestern";
  return `vor ${Math.round(diffSec / 86400)} Tagen`;
}

const BROKERS = [
  { key: "okx",     label: "OKX" },
  { key: "bitget",  label: "Bitget" },
  { key: "binance", label: "Binance" },
  { key: "kraken",  label: "Kraken" },
  { key: "bitunix", label: "Bitunix" },
  { key: "levex",   label: "LeveX" },
];
// Nur Broker mit eigenem /sync-Endpoint (LeveX ist CSV-only und hat keinen Sync).
const SYNC_BROKERS = BROKERS.filter((b) => b.key !== "levex");

const SOURCE_LABEL = {
  manual: "Manuell",
  okx: "OKX",
  bitget: "Bitget",
  binance: "Binance",
  kraken: "Kraken",
  bitunix: "Bitunix",
  levex: "LeveX",
  manual_enriched: "Manuell (angereichert)",
};

const SOURCE_OPTIONS = [
  { v: "manual", label: "Manuell" },
  { v: "manual_enriched", label: "Manuell + Broker" },
  { v: "okx", label: "OKX" },
  { v: "bitget", label: "Bitget" },
  { v: "binance", label: "Binance" },
  { v: "kraken", label: "Kraken" },
  { v: "bitunix", label: "Bitunix" },
  { v: "levex", label: "LeveX" },
];

function computeMatchableManuals(manuals, candidates, windowMs = 10 * 60 * 1000) {
  const out = new Set();
  for (const m of manuals) {
    const mAsset = (m.asset || "").toLowerCase();
    if (!mAsset || !m.opened_at) continue;
    const mTs = new Date(m.opened_at).getTime();
    if (Number.isNaN(mTs)) continue;
    for (const c of candidates) {
      const cAsset = (c.asset || "").toLowerCase();
      if (!cAsset) continue;
      if (!(cAsset === mAsset || cAsset.includes(mAsset) || mAsset.includes(cAsset))) continue;
      if (m.side && c.side && m.side !== c.side) continue;
      const cTs = c.opened_at ? new Date(c.opened_at).getTime() : NaN;
      if (Number.isNaN(cTs)) continue;
      if (Math.abs(cTs - mTs) <= windowMs) { out.add(m.id); break; }
    }
  }
  return out;
}

// Wählt die gemeinsame Quote-Währung aus einer Liste von Trades/Positionen.
// Rückgabe:
//   • einzige eindeutige Währung → dieser String
//   • gemischte oder unbekannte → "" (kein hardcoded Fallback, `fmtMoney` rendert dann ohne Ticker)
function pickCommonCurrency(items) {
  if (!items || !items.length) return "";
  const set = new Set();
  for (const it of items) {
    const c = it?.quote_asset || deriveQuoteCurrency(it?.asset) || "";
    if (c) set.add(c);
  }
  if (set.size === 1) return [...set][0];
  return "";
}

// Prozentuales SL-Risiko relativ zum Ø Entry — für die Listen-Ansicht.
// Long verliert bei fallendem Kurs (SL < Entry → negativ), Short bei steigendem.
function slPctOf(position) {
  const stop = position?.stop;
  const entry = position?.avg_entry;
  if (stop == null || entry == null || entry === 0) return null;
  const isLong = (position.primary_side || "long") === "long";
  const move = ((stop - entry) / entry) * 100;
  return isLong ? move : -move;
}

function fmtSlPct(pct) {
  if (pct == null) return null;
  return `${pct > 0 ? "+" : "−"}${Math.abs(pct).toFixed(2).replace(".", ",")}\u00A0%`;
}

export default function TradingJournal() {
  const [wizardOpen, setWizardOpen] = useState(false);
  const [tab, setTab] = useState("uebersicht");
  const [viewMode, setViewMode] = useState("positions"); // "positions" | "fills"
  const [q, setQ] = useState("");
  const [side, setSide] = useState("all");
  const [srcSet, setSrcSet] = useState(new Set());  // leerer Set = alle
  const [activeManagedOnly, setActiveManagedOnly] = useState(false);
  const [mergeSuggestions, setMergeSuggestions] = useState({ auto: [], needs_confirmation: [] });
  const [trades, setTrades] = useState([]);
  const [positions, setPositions] = useState({ open: [], closed: [] });
  const [loading, setLoading] = useState(true);
  const [syncBusy, setSyncBusy] = useState(null);   // provider name | "all" | null
  const [brokerStatus, setBrokerStatus] = useState({}); // {okx: true, bitget: false, ...}
  const [enrichTrade, setEnrichTrade] = useState(null);
  const [detailTrade, setDetailTrade] = useState(null);
  const [detailPosition, setDetailPosition] = useState(null);
  const [matchableIds, setMatchableIds] = useState(new Set());
  const [lastSyncInfo, setLastSyncInfo] = useState(null); // {perProvider: {okx:{inserted,fetched}}, total}
  const [subAccounts, setSubAccounts] = useState([]);
  const [brokersOverview, setBrokersOverview] = useState({ connections: [], connected_count: 0, last_sync_at: null });

  // Nach jedem Reload den geöffneten Position-Dialog per id neu auflösen,
  // damit Journal-Änderungen sofort sichtbar sind (sonst zeigt der Dialog
  // die eingefrorene Referenz aus dem Row-Klick).
  useEffect(() => {
    if (!detailPosition) return;
    const all = [...(positions.open || []), ...(positions.closed || [])];
    const fresh = all.find((p) => p.id === detailPosition.id);
    if (fresh && fresh !== detailPosition) setDetailPosition(fresh);
  }, [positions]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const statusReqs = BROKERS.map((b) =>
        axios.get(`${API}/api/broker/${b.key}/status`, { withCredentials: true }).catch(() => ({ data: {} })),
      );
      const [tRes, cRes, pRes, subRes, bStatusRes, mergeRes, ...statusResArr] = await Promise.all([
        axios.get(`${API}/api/trades`, { withCredentials: true }),
        axios.get(`${API}/api/trades/broker-candidates`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/trades/positions`, { withCredentials: true }).catch(() => ({ data: { open: [], closed: [] } })),
        axios.get(`${API}/api/trades/sub-account-stats`, { withCredentials: true }).catch(() => ({ data: { accounts: [] } })),
        axios.get(`${API}/api/brokers/status-all`, { withCredentials: true }).catch(() => ({ data: { connections: [], connected_count: 0, last_sync_at: null } })),
        axios.get(`${API}/api/trades/merge-suggestions`, { withCredentials: true }).catch(() => ({ data: { auto: [], needs_confirmation: [] } })),
        ...statusReqs,
      ]);
      setTrades(tRes.data || []);
      setPositions(pRes.data || { open: [], closed: [] });
      setSubAccounts(subRes.data?.accounts || []);
      setBrokersOverview(bStatusRes.data || { connections: [], connected_count: 0, last_sync_at: null });
      setMergeSuggestions(mergeRes.data || { auto: [], needs_confirmation: [] });
      const map = {};
      BROKERS.forEach((b, i) => { map[b.key] = !!statusResArr[i]?.data?.connected; });
      setBrokerStatus(map);
      const manuals = (tRes.data || []).filter((t) => t.provenance === "manual");
      setMatchableIds(computeMatchableManuals(manuals, cRes.data || []));
    } catch {
      toast.error("Trades konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const syncOne = async (provider) => {
    setSyncBusy(provider);
    try {
      const res = await axios.post(`${API}/api/broker/${provider}/sync`, {}, { withCredentials: true });
      await load();
      setLastSyncInfo({
        perProvider: { [provider]: { inserted: res.data.inserted || 0, fetched: res.data.fetched || 0 } },
        total: res.data.inserted || 0,
      });
      toast.success(`${SOURCE_LABEL[provider]}-Sync abgeschlossen`, {
        description: `${res.data.inserted} neu · ${res.data.skipped_existing} vorhanden · ${res.data.fetched} Fills`,
      });
    } catch (e) {
      toast.error("Sync fehlgeschlagen", { description: e?.response?.data?.detail || `${SOURCE_LABEL[provider]} nicht erreichbar.` });
    } finally { setSyncBusy(null); }
  };

  const syncAll = async () => {
    const connected = SYNC_BROKERS.filter((b) => brokerStatus[b.key]);
    if (connected.length === 0) {
      toast.info("Nichts zu synchronisieren", {
        description: "LeveX ist CSV-only. Für andere Broker verbinde zuerst eine API-Verbindung.",
      });
      return;
    }
    setSyncBusy("all");
    const perProvider = {};
    let total = 0;
    for (const b of connected) {
      try {
        const res = await axios.post(`${API}/api/broker/${b.key}/sync`, {}, { withCredentials: true });
        perProvider[b.key] = { inserted: res.data.inserted || 0, fetched: res.data.fetched || 0 };
        total += res.data.inserted || 0;
      } catch (e) {
        perProvider[b.key] = { inserted: 0, fetched: 0, error: e?.response?.data?.detail || "Fehler" };
      }
    }
    await load();
    setLastSyncInfo({ perProvider, total });
    toast.success("Multi-Broker-Sync abgeschlossen", { description: `${total} neue Trades insgesamt` });
    setSyncBusy(null);
  };

  const dismissSyncBanner = () => setLastSyncInfo(null);
  const [repairing, setRepairing] = useState(false);
  const repairLabels = async () => {
    if (!window.confirm(
      "Konten-Labels reparieren?\n\n" +
      "Ich hole die Fills deiner aktiven OKX-Verbindungen und ordne alle Trades " +
      "über die Order-ID dem richtigen Konto zu. Trades, die zu keinem aktiven " +
      "Konto passen, werden gelöscht. Journal-Notizen (Strategie, Reflexion) " +
      "bleiben erhalten."
    )) return;
    setRepairing(true);
    try {
      const res = await axios.post(`${API}/api/broker/okx/repair-labels`, {}, { withCredentials: true });
      const data = res.data || {};
      const parts = Object.entries(data.reassigned || {}).map(([k, v]) => `${k}: ${v}`).join(" · ");
      const failed = (data.failed_connections || []).map((f) => f.label).join(", ");
      const description =
        `${parts || "Keine Trades umsortiert"}` +
        `${data.orphans_deleted ? ` · ${data.orphans_deleted} Waisen gelöscht` : ""}` +
        `${data.orphans_skipped ? ` · ${data.orphans_skipped} Waisen NICHT gelöscht (Sicherheits-Stop)` : ""}`;
      if (failed) {
        toast.warning("Reparatur mit Warnungen", {
          description: `${failed} konnte nicht abgefragt werden. ${description}`,
          duration: 10000,
        });
      } else {
        toast.success("Konten-Labels aktualisiert", { description });
      }
      await load();
    } catch (e) {
      toast.error("Reparatur fehlgeschlagen", { description: e?.response?.data?.detail || "" });
    } finally { setRepairing(false); }
  };
  const firstMatchable = useMemo(() => {
    if (!matchableIds.size) return null;
    return trades.find((t) => t.provenance === "manual" && matchableIds.has(t.id)) || null;
  }, [trades, matchableIds]);

  const toggleSrc = (v) => {
    setSrcSet((prev) => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v); else next.add(v);
      return next;
    });
  };
  const clearSrc = () => setSrcSet(new Set());

  const filtered = useMemo(() => {
    return trades.filter((t) => {
      if (side !== "all" && (t.side || "").toLowerCase() !== side) return false;
      if (srcSet.size > 0 && !srcSet.has(t.provenance)) return false;
      if (q) {
        const s = q.toLowerCase();
        const hay = `${t.asset || ""} ${t.strategy_name || ""} ${t.id || ""}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [trades, q, side, srcSet]);

  const filteredPositions = useMemo(() => {
    const filterPos = (list) => list.filter((p) => {
      if (side !== "all") {
        // "Long+Short" (mixed) immer zeigen; sonst nach primary_side filtern
        if ((p.sides || []).length === 1 && p.primary_side !== side) return false;
      }
      if (srcSet.size > 0) {
        const anyMatch = (p.provenances || []).some((prov) => srcSet.has(prov));
        if (!anyMatch) return false;
      }
      if (activeManagedOnly && !((p.refills || 0) >= 2 || (p.partial_exits || 0) >= 1)) {
        return false;
      }
      if (q) {
        const s = q.toLowerCase();
        const hay = `${p.asset || ""} ${p.strategy_name || ""} ${p.broker_label || ""} ${p.id || ""} ${p.trade_number || ""}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
    return { open: filterPos(positions.open || []), closed: filterPos(positions.closed || []) };
  }, [positions, q, side, srcSet, activeManagedOnly]);

  const totalPl = filtered.reduce((a, t) => a + (t.pl || 0), 0);
  const wins = filtered.filter((t) => (t.pl || 0) > 0).length;
  const winrate = filtered.length ? Math.round((wins / filtered.length) * 100) : 0;
  const drafts = trades.filter((t) => t.status === "entwurf");
  const archived = trades.filter((t) => t.status === "archiviert");
  const totalPositionsCount = (positions.open?.length || 0) + (positions.closed?.length || 0);

  const perProviderCount = useMemo(() => {
    const acc = {};
    for (const t of trades) acc[t.provenance] = (acc[t.provenance] || 0) + 1;
    return acc;
  }, [trades]);

  const tabs = [
    { key: "uebersicht", label: "Übersicht" },
    { key: "trades", label: "Trades", count: viewMode === "positions" ? totalPositionsCount : trades.length },
    { key: "entwuerfe", label: "Entwürfe", count: drafts.length },
    { key: "archiv", label: "Archiv", count: archived.length },
  ];

  const anyBrokerConnected = Object.values(brokerStatus).some(Boolean);

  return (
    <div>
      <PageHeader
        eyebrow="Trading Journal"
        title="Trading Journal"
        description="Manuelle Trades und Broker-Sync in einer Ansicht. Broker-Daten überschreiben deine Eingaben nur nach Bestätigung."
        testid="journal-header"
        actions={
          <div className="flex gap-2 flex-wrap items-center">
            {brokersOverview.last_sync_at && (
              <span
                className="text-xs text-muted-foreground font-mono flex items-center gap-1.5 px-3"
                title={new Date(brokersOverview.last_sync_at).toLocaleString("de-DE")}
                data-testid="journal-last-sync"
              >
                <span className="h-2 w-2 rounded-full bg-[hsl(122_39%_49%)]" />
                Zuletzt gesynct {fmtRelative(brokersOverview.last_sync_at)}
              </span>
            )}
            {anyBrokerConnected && (
              <Button
                variant="outline"
                className="rounded-full h-11 px-5"
                onClick={syncAll}
                disabled={!!syncBusy}
                data-testid="journal-sync-all-btn"
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${syncBusy === "all" ? "animate-spin" : ""}`} strokeWidth={1.5} />
                {syncBusy === "all" ? "Sync läuft…" : `Alle Broker syncen${brokersOverview.connected_count > 0 ? ` (${brokersOverview.connected_count})` : ""}`}
              </Button>
            )}
            <Button
              className="rounded-full h-11 px-5"
              onClick={() => setWizardOpen(true)}
              data-testid="open-wizard-btn"
            >
              <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} /> Neuer Trade
            </Button>
          </div>
        }
      />

      <SecondaryNav tabs={tabs} active={tab} onChange={setTab} testid="journal-secondary-nav" />

      {lastSyncInfo && (
        <div
          className="mb-6 rounded-2xl border border-primary/40 bg-primary/10 p-5 flex flex-col md:flex-row md:items-center gap-4"
          data-testid="sync-summary-banner"
        >
          <div className="flex-1">
            <div className="font-heading text-lg">Sync abgeschlossen</div>
            <p className="text-sm text-muted-foreground mt-1">
              {lastSyncInfo.total > 0
                ? `${lastSyncInfo.total} neue Trades geladen`
                : "Keine neuen Trades — alles ist auf dem Stand deines letzten Sync"}
              {matchableIds.size > 0 && (
                <>
                  {" · "}
                  <span className="text-foreground font-medium">
                    {matchableIds.size} passen zu bestehenden manuellen Einträgen
                  </span>
                </>
              )}
            </p>
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              {Object.entries(lastSyncInfo.perProvider).map(([p, s]) => (
                <span
                  key={p}
                  className={`rounded-full border px-3 py-1 ${s.error ? "border-[hsl(0_66%_50%/0.4)] text-[hsl(0_66%_72%)]" : "border-border text-foreground"}`}
                  data-testid={`sync-banner-provider-${p}`}
                >
                  <span className="font-medium">{SOURCE_LABEL[p]}:</span>{" "}
                  {s.error ? "Fehler" : `${s.inserted} neu · ${s.fetched} Fills`}
                </span>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {firstMatchable && (
              <Button className="rounded-full h-10" onClick={() => setEnrichTrade(firstMatchable)} data-testid="sync-banner-enrich">
                <Link2 className="mr-2 h-4 w-4" strokeWidth={1.5} /> Jetzt anreichern
              </Button>
            )}
            <Button variant="ghost" className="rounded-full h-10" onClick={dismissSyncBanner} data-testid="sync-banner-dismiss">
              Schließen
            </Button>
          </div>
        </div>
      )}

      {tab === "uebersicht" && (
        <>
          <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <KpiCard label="Trades gesamt" value={trades.length} />
            <KpiCard label="Gefiltert P/L" value={fmtMoney(totalPl, pickCommonCurrency(filtered), { signed: true })} />
            <KpiCard label="Trefferquote" value={`${winrate}\u00A0%`} />
            <KpiCard
              label="Broker-Trades"
              value={BROKERS.reduce((n, b) => n + (perProviderCount[b.key] || 0), 0)}
              testid="kpi-broker-trades"
            />
          </section>
          {subAccounts.length >= 1 && (
            <SubAccountsBlock accounts={subAccounts} onRepair={brokerStatus.okx ? repairLabels : null} repairing={repairing} />
          )}
          <TradesSection
            q={q} setQ={setQ}
            side={side} setSide={setSide}
            srcSet={srcSet} toggleSrc={toggleSrc} clearSrc={clearSrc}
            perProviderCount={perProviderCount}
            filtered={filtered}
            positions={filteredPositions}
            viewMode={viewMode} setViewMode={setViewMode}
            loading={loading}
            openWizard={() => setWizardOpen(true)}
            onReload={load}
            onEnrich={setEnrichTrade}
            onOpenDetail={setDetailTrade}
            onOpenPosition={setDetailPosition}
            anyBrokerConnected={anyBrokerConnected}
            activeManagedOnly={activeManagedOnly}
            setActiveManagedOnly={setActiveManagedOnly}
            mergeSuggestions={mergeSuggestions}
            onMergeClick={(broker_id) => {
              const bt = trades.find((t) => t.id === broker_id);
              if (bt) setEnrichTrade(null);
            }}
          />
        </>
      )}

      {tab === "trades" && (
        <TradesSection
          q={q} setQ={setQ}
          side={side} setSide={setSide}
          srcSet={srcSet} toggleSrc={toggleSrc} clearSrc={clearSrc}
          perProviderCount={perProviderCount}
          filtered={filtered}
          positions={filteredPositions}
          viewMode={viewMode} setViewMode={setViewMode}
          loading={loading}
          openWizard={() => setWizardOpen(true)}
          onReload={load}
          onEnrich={setEnrichTrade}
          onOpenDetail={setDetailTrade}
          onOpenPosition={setDetailPosition}
          anyBrokerConnected={anyBrokerConnected}
          activeManagedOnly={activeManagedOnly}
          setActiveManagedOnly={setActiveManagedOnly}
          mergeSuggestions={mergeSuggestions}
        />
      )}

      {tab === "entwuerfe" && (
        <EmptyBlock
          icon={FileEdit}
          title={drafts.length === 0 ? "Keine Entwürfe" : `${drafts.length} Entwürfe`}
          text="Angefangene, aber noch nicht abgeschlossene Trade-Erfassungen erscheinen hier."
          testid="drafts-empty"
        >
          <Button variant="outline" className="rounded-full mt-5" onClick={() => setWizardOpen(true)} data-testid="drafts-new-btn">
            <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} /> Neuen Trade beginnen
          </Button>
        </EmptyBlock>
      )}

      {tab === "archiv" && (
        <EmptyBlock
          icon={Archive}
          title="Archiv ist leer"
          text="Archivierte Trades bleiben durchsuchbar, tauchen aber nicht in aktiven Auswertungen auf."
          testid="archive-empty"
        />
      )}

      <TradeWizard open={wizardOpen} onOpenChange={(o) => { setWizardOpen(o); if (!o) load(); }} />
      <EnrichDialog
        open={!!enrichTrade}
        onOpenChange={(o) => { if (!o) setEnrichTrade(null); }}
        manualTrade={enrichTrade}
        onEnriched={load}
      />
      <TradeDetailDialog
        trade={detailTrade}
        open={!!detailTrade}
        onOpenChange={(o) => { if (!o) setDetailTrade(null); }}
        onDeleted={load}
        onUpdated={(t) => { setDetailTrade(t); load(); }}
      />
      <PositionDetailDialog
        position={detailPosition}
        open={!!detailPosition}
        onOpenChange={(o) => { if (!o) setDetailPosition(null); }}
        onUpdated={load}
      />
    </div>
  );
}

function EmptyBlock({ icon: Icon, title, text, children, testid }) {
  return (
    <div className="card-surface p-10 text-center" data-testid={testid}>
      <Icon className="mx-auto h-8 w-8 text-muted-foreground" strokeWidth={1.5} />
      <div className="font-heading text-lg mt-3">{title}</div>
      <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">{text}</p>
      {children}
    </div>
  );
}

function KpiCard({ label, value, testid }) {
  return (
    <div className="card-surface p-5" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className="font-mono text-2xl mt-2">{value}</div>
    </div>
  );
}

function SubAccountsBlock({ accounts, onRepair, repairing }) {
  return (
    <section className="mb-8" data-testid="sub-accounts-block">
      <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-baseline gap-3">
          <h3 className="font-heading text-lg">Nach Konto</h3>
          <span className="text-xs text-muted-foreground">
            {accounts.length} Konten
          </span>
        </div>
        {onRepair && (
          <Button
            variant="outline"
            size="sm"
            className="rounded-full h-8 text-xs"
            onClick={onRepair}
            disabled={repairing}
            data-testid="sub-accounts-repair"
            title="Ordnet OKX-Trades ohne Label über die Order-ID den aktiven Konten zu und löscht Waisen."
          >
            {repairing ? <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" strokeWidth={1.5} /> Ordne zu …</> : <><Wrench className="mr-2 h-3.5 w-3.5" strokeWidth={1.5} /> Konten-Labels reparieren</>}
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {accounts.map((a) => {
          const plColor = a.total_pl == null ? "" : a.total_pl >= 0 ? "text-[hsl(122_39%_65%)]" : "text-[hsl(0_66%_72%)]";
          const wrColor = a.win_rate == null ? "text-muted-foreground" : a.win_rate >= 50 ? "text-[hsl(122_39%_65%)]" : "text-[hsl(43_74%_66%)]";
          const testKey = `sub-account-${a.provenance_group}-${a.label.toLowerCase().replace(/\s+/g, "-")}`;
          return (
            <div
              key={`${a.provenance_group}-${a.label}`}
              className="card-surface p-4"
              data-testid={testKey}
            >
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-medium text-sm">{a.label}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                    {a.provenance_group === "broker" ? a.provenances.map((p) => SOURCE_LABEL[p] || p).join(" · ") : "Manuell"}
                  </div>
                </div>
                <span className="text-[10px] font-mono text-muted-foreground rounded-full border border-border px-2 py-0.5">
                  {a.trade_count} Trades
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Netto P/L</div>
                  <div className={`font-mono text-sm mt-1 ${plColor}`} data-testid={`${testKey}-pl`}>
                    {a.total_pl != null ? fmtMoney(a.total_pl, a.quote_asset || "", { signed: true }) : "—"}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Trefferquote</div>
                  <div className={`font-mono text-sm mt-1 ${wrColor}`} data-testid={`${testKey}-winrate`}>
                    {a.win_rate != null ? `${a.win_rate.toFixed(0)}\u00A0%` : "—"}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Gebühren</div>
                  <div className="font-mono text-sm mt-1 text-muted-foreground">
                    {a.total_fees > 0 ? fmtMoney(a.total_fees, a.quote_asset || "") : "—"}
                  </div>
                </div>
              </div>
              {a.last_activity_at && (
                <div className="text-[10px] text-muted-foreground mt-3 font-mono">
                  Aktivität: {fmtRelative(a.last_activity_at)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function TradesSection({ q, setQ, side, setSide, srcSet, toggleSrc, clearSrc, perProviderCount, filtered, positions, viewMode, setViewMode, loading, openWizard, onEnrich, onOpenDetail, onOpenPosition, anyBrokerConnected, activeManagedOnly, setActiveManagedOnly, mergeSuggestions, onMergeClick }) {
  const selectedLabel = srcSet.size === 0
    ? "Alle Quellen"
    : Array.from(srcSet).map((v) => SOURCE_LABEL[v] || v).slice(0, 2).join(", ") + (srcSet.size > 2 ? ` +${srcSet.size - 2}` : "");

  const showPositions = viewMode === "positions";
  const hasAny = showPositions
    ? (positions.open.length + positions.closed.length) > 0
    : filtered.length > 0;

  return (
    <section className="card-surface p-4 md:p-6">
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Trade-Nr., Asset, Strategie oder Trade-ID …"
            className="pl-9 h-11"
            data-testid="journal-search-input"
          />
        </div>
        <Select value={side} onValueChange={setSide}>
          <SelectTrigger className="w-full md:w-40 h-11" data-testid="filter-side">
            <SelectValue placeholder="Seite" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Seiten</SelectItem>
            <SelectItem value="long">Long</SelectItem>
            <SelectItem value="short">Short</SelectItem>
          </SelectContent>
        </Select>
        {/* View-Mode Toggle */}
        <div className="inline-flex rounded-full border border-border p-1 bg-secondary/30" data-testid="journal-view-toggle">
          <button
            type="button"
            onClick={() => setViewMode("positions")}
            className={`rounded-full h-9 px-3 text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
              showPositions ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="journal-view-positions"
          >
            <Layers className="h-3.5 w-3.5" strokeWidth={1.5} /> Positionen
          </button>
          <button
            type="button"
            onClick={() => setViewMode("fills")}
            className={`rounded-full h-9 px-3 text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
              !showPositions ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="journal-view-fills"
          >
            <ListIcon className="h-3.5 w-3.5" strokeWidth={1.5} /> Einzel-Fills
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="filter-source-chips">
        <Filter className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
        <span className="text-xs uppercase tracking-wider text-muted-foreground mr-1">Quelle</span>
        <button
          type="button"
          onClick={clearSrc}
          className={`rounded-full border px-3 h-8 text-xs transition-colors ${
            srcSet.size === 0
              ? "border-primary/60 bg-primary/15 text-foreground"
              : "border-border bg-secondary/40 text-muted-foreground hover:text-foreground"
          }`}
          data-testid="filter-source-all"
          aria-label="Alle Quellen anzeigen"
        >
          Alle
        </button>
        {SOURCE_OPTIONS.map((opt) => {
          const count = perProviderCount[opt.v] || 0;
          const active = srcSet.has(opt.v);
          return (
            <button
              key={opt.v}
              type="button"
              onClick={() => toggleSrc(opt.v)}
              className={`rounded-full border px-3 h-8 text-xs transition-colors ${
                active
                  ? "border-primary/60 bg-primary/15 text-foreground"
                  : "border-border bg-secondary/40 text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`filter-source-${opt.v}`}
              aria-pressed={active}
            >
              {opt.label}
              {count > 0 && <span className="ml-1 opacity-70 font-mono">{count}</span>}
            </button>
          );
        })}
        {srcSet.size > 0 && (
          <span className="text-[11px] text-muted-foreground ml-1" data-testid="filter-source-summary">
            Aktiv: {selectedLabel}
          </span>
        )}
        <button
          type="button"
          onClick={() => setActiveManagedOnly((v) => !v)}
          className={`rounded-full border px-3 h-8 text-xs transition-colors ml-2 ${
            activeManagedOnly
              ? "border-primary/60 bg-primary/15 text-foreground"
              : "border-border bg-secondary/40 text-muted-foreground hover:text-foreground"
          }`}
          data-testid="filter-active-managed"
          aria-pressed={activeManagedOnly}
          title="Nur Trades mit ≥ 2 Nachkäufen oder ≥ 1 Teilverkauf"
        >
          Aktiv gemanagt
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-sm text-muted-foreground" data-testid="journal-loading">
          Trades werden geladen…
        </div>
      ) : (
        <>
          {mergeSuggestions && (mergeSuggestions.auto?.length > 0 || mergeSuggestions.needs_confirmation?.length > 0) && (
            <div className="mb-5 rounded-xl border border-[hsl(43_74%_49%/0.4)] bg-[hsl(43_74%_49%/0.08)] p-4" data-testid="merge-suggestions-banner">
              <div className="text-sm font-medium mb-1.5 text-[hsl(43_74%_66%)]">
                {mergeSuggestions.auto.length + mergeSuggestions.needs_confirmation.length} mögliche Zusammenführung{(mergeSuggestions.auto.length + mergeSuggestions.needs_confirmation.length) === 1 ? "" : "en"} erkannt
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {mergeSuggestions.auto.length > 0 && `${mergeSuggestions.auto.length} manuelle Trade${mergeSuggestions.auto.length === 1 ? "" : "s"} passen exakt zu einem Broker-Fill (Preis-Abweichung ≤ 0,1 %). `}
                {mergeSuggestions.needs_confirmation.length > 0 && `${mergeSuggestions.needs_confirmation.length} weitere Match${mergeSuggestions.needs_confirmation.length === 1 ? "" : "es"} mit Preis-Abweichung 0,1–2 % — bitte prüfen. `}
                Öffne den betreffenden manuellen Trade und klick auf „Anreichern", um die Zusammenführung zu bestätigen.
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="merge-suggestions-list">
                {[...(mergeSuggestions.auto || []), ...(mergeSuggestions.needs_confirmation || [])].slice(0, 6).map((m, i) => (
                  <span key={`${m.manual_id}-${m.broker_id}-${i}`} className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/40 px-2 py-0.5 text-[11px] font-mono">
                    {m.asset} · Δ{m.diff_pct}%
                  </span>
                ))}
              </div>
            </div>
          )}
          {!hasAny ? (
        <div className="text-center py-16 border border-dashed border-border rounded-2xl" data-testid="journal-empty">
          <div className="font-heading text-lg">Willkommen im Trading Journal</div>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Dein erster Trade bildet die Grundlage für deine persönliche Entwicklung.
          </p>
          <Button variant="outline" className="mt-5 rounded-full" onClick={openWizard} data-testid="empty-new-trade-btn">
            <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} /> Trade erfassen
          </Button>
        </div>
      ) : showPositions ? (
        <PositionsView positions={positions} onOpenPosition={onOpenPosition} />
      ) : (
        <FillsTable filtered={filtered} onOpenDetail={onOpenDetail} onEnrich={onEnrich} anyBrokerConnected={anyBrokerConnected} />
      )}
        </>
      )}
    </section>
  );
}

function PositionsView({ positions, onOpenPosition }) {
  return (
    <div className="space-y-6" data-testid="positions-view">
      {positions.open.length > 0 && (
        <PositionsBlock
          title="Offene Positionen"
          subtitle="Nicht vollständig geschlossen — Buy- und Sell-Menge sind ungleich."
          items={positions.open}
          onOpenPosition={onOpenPosition}
          testid="positions-open"
          openStyle
        />
      )}
      {positions.closed.length > 0 && (
        <PositionsBlock
          title="Geschlossene Positionen"
          subtitle="Vollständig aufgelöst — Netto-P/L nach Gebühren."
          items={positions.closed}
          onOpenPosition={onOpenPosition}
          testid="positions-closed"
        />
      )}
      {positions.open.length === 0 && positions.closed.length === 0 && (
        <div className="text-center py-12 text-sm text-muted-foreground" data-testid="positions-empty">
          Keine Positionen entsprechen den aktuellen Filtern.
        </div>
      )}
    </div>
  );
}

function PositionsBlock({ title, subtitle, items, onOpenPosition, testid, openStyle }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline gap-3 mb-3">
        <h3 className={`font-heading text-lg ${openStyle ? "text-[hsl(43_74%_66%)]" : ""}`}>{title}</h3>
        <span className="text-xs text-muted-foreground">{items.length} · {subtitle}</span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[110px]">Trade-Nr.</TableHead>
              <TableHead>Asset</TableHead>
              <TableHead>Richtung</TableHead>
              <TableHead className="text-right">Ø Entry</TableHead>
              <TableHead className="text-right">Ø Exit</TableHead>
              <TableHead className="text-right">Größe</TableHead>
              <TableHead className="text-right">SL</TableHead>
              <TableHead className="text-right">Netto P/L</TableHead>
              <TableHead>Aktivität</TableHead>
              <TableHead>Börse</TableHead>
              <TableHead className="text-right w-[40px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((p) => {
              const isMixed = (p.sides || []).length > 1;
              const primaryProv = p.provenances?.[0] || "manual";
              return (
                <TableRow
                  key={p.id}
                  className="hover:bg-secondary/60 transition-colors cursor-pointer"
                  data-testid={`position-row-${p.id}`}
                  onClick={() => onOpenPosition?.(p)}
                >
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {p.trade_number || "—"}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{p.asset}</div>
                    <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                      {p.market}
                      {p.broker_label ? ` · ${p.broker_label}` : ""}
                    </div>
                  </TableCell>
                  <TableCell>
                    {isMixed ? (
                      <Badge variant="secondary" className="rounded-full text-[10px]">Long+Short</Badge>
                    ) : p.primary_side === "long" ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(122_39%_49%/0.15)] text-[hsl(122_39%_65%)] px-2 py-0.5 text-[11px]">
                        <TrendingUp className="h-3 w-3" strokeWidth={1.5} /> Long
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(0_66%_50%/0.15)] text-[hsl(0_66%_72%)] px-2 py-0.5 text-[11px]">
                        <TrendingDown className="h-3 w-3" strokeWidth={1.5} /> Short
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {p.avg_entry != null ? fmtNum(p.avg_entry, 4) : "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {p.avg_exit != null ? fmtNum(p.avg_exit, 4) : "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    <div>{fmtNum(p.size_total, 2)}</div>
                    {p.avg_entry != null && p.size_total != null && (
                      <div className="text-[10px] text-muted-foreground">
                        ≈ {fmtMoney(p.size_total * p.avg_entry, p.quote_asset || deriveQuoteCurrency(p.asset) || "")}
                      </div>
                    )}
                    {p.open_size > 0 && (
                      <div className="text-[10px] text-[hsl(43_74%_66%)]">offen: {fmtNum(p.open_size, 2)}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs" data-testid={`position-row-sl-${p.id}`}>
                    {p.stop != null ? (
                      <>
                        <div>{fmtNum(p.stop, 4)}</div>
                        {slPctOf(p) != null && (
                          <div className={`text-[10px] ${slPctOf(p) < 0 ? "text-[hsl(0_66%_72%)]" : "text-[hsl(122_39%_65%)]"}`}>
                            {fmtSlPct(slPctOf(p))}
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="text-muted-foreground/50">—</span>
                    )}
                  </TableCell>
                  <TableCell className={`text-right font-mono ${p.total_pl == null ? "text-muted-foreground" : (p.total_pl >= 0 ? "text-[hsl(122_39%_65%)]" : "text-[hsl(0_66%_72%)]")}`}>
                    {p.total_pl != null ? fmtMoney(p.total_pl, p.quote_asset || deriveQuoteCurrency(p.asset) || "", { signed: true }) : "—"}
                  </TableCell>
                  <TableCell className="text-[10px] text-muted-foreground">
                    {(p.refills > 0 || p.partial_exits > 0) ? (
                      <div className="flex flex-col gap-0.5">
                        {p.refills > 0 && <span>{p.refills}× Nachkauf</span>}
                        {p.partial_exits > 0 && <span>{p.partial_exits}× Teilverkauf</span>}
                      </div>
                    ) : (
                      <span className="text-muted-foreground/50">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <ProvenanceBadge
                      source={SOURCE_LABEL[primaryProv] || primaryProv}
                      label={p.broker_label}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" strokeWidth={1.5} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function FillsTable({ filtered, onOpenDetail, onEnrich, anyBrokerConnected }) {
  return (
    <div className="overflow-x-auto">
      <Table data-testid="trades-table">
        <TableHeader>
          <TableRow>
            <TableHead>Asset</TableHead>
            <TableHead>Seite</TableHead>
            <TableHead>Eröffnet</TableHead>
            <TableHead className="text-right">Entry</TableHead>
            <TableHead className="text-right">Exit</TableHead>
            <TableHead className="text-right">P/L</TableHead>
            <TableHead>Strategie</TableHead>
            <TableHead>Quelle</TableHead>
            <TableHead className="text-right w-[80px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((t) => (
            <TableRow
              key={t.id}
              className="hover:bg-secondary/60 transition-colors cursor-pointer"
              data-testid={`trade-row-${t.id}`}
              onClick={() => onOpenDetail?.(t)}
            >
              <TableCell>
                <div className="font-medium flex items-center gap-2">
                  {t.asset || "—"}
                  {t.attachments?.length > 0 && (
                    <span
                      className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground font-mono"
                      title={`${t.attachments.length} Anhang${t.attachments.length === 1 ? "" : "e"}`}
                      data-testid={`trade-row-attach-${t.id}`}
                    >
                      <Paperclip className="h-3 w-3" strokeWidth={1.5} />
                      {t.attachments.length}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground font-mono truncate max-w-[180px]">
                  {t.market} · {t.id.slice(0, 12)}
                </div>
              </TableCell>
              <TableCell>
                <Badge variant="secondary" className="rounded-full font-medium capitalize">
                  {t.side}
                </Badge>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {t.opened_at ? fmtDateTime(t.opened_at) : "—"}
              </TableCell>
              <TableCell className="text-right font-mono">
                {t.entry !== null && t.entry !== undefined ? fmtNum(t.entry, 4) : "—"}
              </TableCell>
              <TableCell className="text-right font-mono">
                {t.exit !== null && t.exit !== undefined ? fmtNum(t.exit, 4) : "—"}
              </TableCell>
              <TableCell className={`text-right font-mono ${(t.pl || 0) >= 0 ? "text-[hsl(122_39%_65%)]" : "text-[hsl(0_66%_72%)]"}`}>
                {t.pl !== null && t.pl !== undefined ? fmtMoney(t.pl, t.quote_asset || deriveQuoteCurrency(t.asset) || "", { signed: true }) : "—"}
              </TableCell>
              <TableCell className="text-sm">{t.strategy_name || "—"}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <ProvenanceBadge source={SOURCE_LABEL[t.provenance] || t.provenance} label={t.broker_label} />
                  {t.provenance === "manual_enriched" && (
                    <Link2 className="h-3.5 w-3.5 text-[hsl(122_39%_65%)]" strokeWidth={1.5} />
                  )}
                </div>
              </TableCell>
              <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                {t.provenance === "manual" && anyBrokerConnected && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 rounded-full px-3 text-xs"
                    onClick={() => onEnrich?.(t)}
                    data-testid={`trade-enrich-${t.id}`}
                    title="Mit Broker-Fill anreichern"
                  >
                    <Link2 className="mr-1 h-3.5 w-3.5" strokeWidth={1.5} /> Anreichern
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
